import { buildLeadLockedHint, buildLeadSummary } from './chatArtifactLeadModel';
import {
  normalizeGeneratedCell,
  normalizeGeneratedColumns,
} from './chatArtifactGeneratedAnswers';
import {
  normalizeEligibilityCounts,
  normalizeNotices,
} from './chatArtifactEligibilityModel';
import { artifactExportScopeCount } from './chatArtifactExportModel';
import { formatLocation, firstNumber, firstPresent, listLiteralText, reportedHeadcountText, reportedText, safeText } from './chatArtifactValueUtils';
import { artifactTableLabel, coreResultSetFromPayload, displayPreviewRows, resolveCoreCandidates } from './coreResultSetModel';

const DEFAULT_COLUMNS = [
  { key: 'business_name', label: 'Business Name', minWidth: 160 },
  { key: 'domain', label: 'Domain', minWidth: 150 },
  { key: 'location', label: 'Location', minWidth: 180 },
  { key: 'employees', label: 'Employees', minWidth: 120 },
  { key: 'revenue', label: 'Revenue', minWidth: 120 },
  { key: 'description', label: 'Description', minWidth: 260 },
];

const FIND_SIMILAR_SUGGESTION = {
  key: 'find_similar',
  label: 'Find similar companies',
  prompt: 'Find similar companies to this list.',
};

const MAX_REVENUE_BOUND = 1_000_000_000_000;

function plainText(value) {
  return safeText(value, '');
}

function normalizeUrl(rawValue) {
  const raw = plainText(rawValue).trim();
  if (!raw || raw === 'N/A') return { domain: 'N/A', domain_url: undefined };

  const withProtocol = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withProtocol);
    if (!url.hostname || !url.hostname.includes('.')) {
      return { domain: raw.replace(/^https?:\/\//i, '').replace(/\/$/, '') || 'N/A', domain_url: undefined };
    }
    const domain = url.hostname.replace(/^www\./i, '');
    return {
      domain,
      domain_url: `https://${domain}`,
    };
  } catch (_error) {
    return {
      domain: raw.replace(/^https?:\/\//i, '').replace(/\/$/, '') || 'N/A',
      domain_url: undefined,
    };
  }
}
function compactNumber(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return undefined;
  if (Math.abs(number) >= 1000000000) return `${Number((number / 1000000000).toFixed(1))}B`;
  if (Math.abs(number) >= 1000000) return `${Number((number / 1000000).toFixed(1))}M`;
  if (Math.abs(number) >= 1000) return `${Number((number / 1000).toFixed(1))}K`;
  return number.toLocaleString();
}
function formatRange(lower, upper, options = {}) {
  const lowerNumber = normalizedBound(firstNumber(lower), options);
  const upperNumber = normalizedBound(firstNumber(upper), options);
  if (lowerNumber !== undefined && upperNumber !== undefined) {
    const left = options.money ? `$${compactNumber(lowerNumber)}` : lowerNumber.toLocaleString();
    const right = options.money ? `$${compactNumber(upperNumber)}` : upperNumber.toLocaleString();
    return `${left}–${right}`;
  }
  const point = lowerNumber ?? upperNumber;
  if (point === undefined) return undefined;
  return options.money ? `$${compactNumber(point)}` : point.toLocaleString();
}
function normalizedBound(value, options = {}) {
  if (value === undefined) return undefined;
  if (options.money && Math.abs(value) > MAX_REVENUE_BOUND) return undefined;
  return value;
}
function parseMoneyDisplayPart(value) {
  const match = String(value || '').trim().match(/^\$?\s*([+-]?\d+(?:,\d{3})*(?:\.\d+)?|[+-]?\d+(?:\.\d+)?)\s*([KMBT])?$/i);
  if (!match) return null;
  const amount = Number(match[1].replace(/,/g, ''));
  if (!Number.isFinite(amount)) return null;
  const multiplier = {
    K: 1_000,
    M: 1_000_000,
    B: 1_000_000_000,
    T: 1_000_000_000_000,
  }[String(match[2] || '').toUpperCase()] || 1;
  return amount * multiplier;
}
function cleanRevenueDisplay(value) {
  const text = plainText(value).trim();
  if (!text) return '';
  const parts = text.split(/\s*[-–]\s*/).filter(Boolean);
  const kept = parts.filter((part) => {
    const parsed = parseMoneyDisplayPart(part);
    return parsed === null || Math.abs(parsed) <= MAX_REVENUE_BOUND;
  });
  return kept.join('–');
}

function candidateId(candidate) {
  const metadata = candidate?.metadata || {};
  const company = candidate?.company || candidate?.company_snapshot || {};
  return firstPresent(
    candidate?.company_id,
    candidate?.id,
    candidate?._id,
    company?.id,
    company?._id,
    metadata?.company_id
  );
}

function candidateCompany(candidate) {
  return candidate?.company || candidate?.company_snapshot || {};
}

function candidateMetadata(candidate) {
  return candidate?.metadata || {};
}

function candidateDisplay(candidate) {
  return candidate?.display || {};
}

function employeeDisplay(candidate) {
  const displayValues = candidateDisplay(candidate);
  const metadata = candidateMetadata(candidate);
  const company = candidateCompany(candidate);
  const display = firstPresent(
    displayValues?.employees,
    candidate?.employee_display,
    candidate?.employee_count_display,
    candidate?.employee_range,
    candidate?.employees,
    candidate?.headcount,
    metadata?.employee_display,
    metadata?.employee_range,
    company?.employee_display,
    company?.employee_range,
    company?.employees,
    company?.headcount
  );
  if (display) return reportedHeadcountText(display);

  return formatRange(
    firstPresent(candidate?.employee_count_lower, candidate?.employee_lower, company?.employee_count_lower),
    firstPresent(candidate?.employee_count_upper, candidate?.employee_upper, company?.employee_count_upper)
  ) || reportedText(firstPresent(candidate?.employee_count, company?.employee_count));
}

function revenueDisplay(candidate) {
  const displayValues = candidateDisplay(candidate);
  const metadata = candidateMetadata(candidate);
  const company = candidateCompany(candidate);
  const display = firstPresent(
    displayValues?.revenue,
    candidate?.revenue_display,
    candidate?.revenue_range,
    metadata?.revenue_display,
    metadata?.revenue_range,
    company?.revenue_display,
    company?.revenue_range,
    company?.revenue
  );
  if (display) return reportedText(cleanRevenueDisplay(display));

  return formatRange(
    firstPresent(candidate?.revenue_lower, candidate?.revenue_min, company?.revenue_lower),
    firstPresent(candidate?.revenue_upper, candidate?.revenue_max, company?.revenue_upper),
    { money: true }
  ) || reportedText(candidate?.revenue);
}

function locationDisplay(candidate) {
  const metadata = candidateMetadata(candidate);
  const company = candidateCompany(candidate);
  return safeText(
    firstPresent(
      candidate?.location_display,
      metadata?.primary_location_display,
      company?.primary_location_display,
      formatLocation(metadata?.primary_location),
      formatLocation(metadata?.primaryLocation),
      formatLocation(candidate?.primary_location),
      formatLocation(candidate?.primaryLocation),
      formatLocation(candidate?.location),
      formatLocation(company?.primary_location),
      formatLocation(company?.primaryLocation),
      formatLocation(company?.location),
      candidate?.hq,
      company?.hq
    )
  );
}

function candidateToDisplayRow(candidate) {
  const metadata = candidateMetadata(candidate);
  const company = candidateCompany(candidate);
  const domain = normalizeUrl(
    firstPresent(
      candidate?.domain,
      candidate?.website,
      candidate?.primaryWebsite,
      metadata?.domain,
      metadata?.website,
      metadata?.primaryWebsite,
      metadata?.primary_website,
      company?.domain,
      company?.website,
      company?.primaryWebsite
    )
  );

  const row = {
    id: safeText(candidateId(candidate), ''),
    business_name: safeText(
      firstPresent(
        candidate?.company_name,
        company?.name,
        company?.company_name,
        metadata?.name,
        candidate?.name
      ),
      'Unknown company'
    ),
    domain: domain.domain,
    domain_url: domain.domain_url,
    location: locationDisplay(candidate),
    employees: employeeDisplay(candidate),
    employees_estimated: candidateDisplay(candidate).employees_estimated === true,
    revenue: revenueDisplay(candidate),
    description: safeText(listLiteralText(firstPresent(metadata?.description, candidate?.descriptive, candidate?.description))),
    ...(candidate?.lead_finder_contacts?.some((person) => person?.field_status)
      ? {leadContacts: candidate.lead_finder_contacts} : {}),
  };

  Object.entries(candidate?.enrichment_columns || {}).forEach(([label, value]) => {
    const key = label
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '_')
      .replace(/^_+|_+$/g, '');
    if (key) row[key] = safeText(value);
  });

  Object.entries(candidate?.generated_cells || {}).forEach(([key, cell]) => {
    if (key) row[key] = normalizeGeneratedCell(cell);
  });

  return row;
}

function companyToCandidate(company) {
  return {
    company_id: company?.id,
    company_name: company?.name,
    website: company?.domain || company?.website,
    location_display: company?.hq,
    employee_display: company?.headcount || company?.employees,
    revenue_display: company?.revenue,
  };
}

function normalizeColumn(column) {
  if (!column || typeof column !== 'object') return null;
  const key = firstPresent(column.key, column.field);
  const label = firstPresent(column.label, column.headerName, key);
  if (!key || !label) return null;
  return {
    key,
    label,
    minWidth: firstNumber(column.minWidth, column.min_width) || 120,
  };
}

function normalizeColumns(artifact) {
  const columns = firstPresent(artifact?.preview_columns, artifact?.display_columns);
  const baseColumns = Array.isArray(columns) && columns.length > 0
    ? columns.map(normalizeColumn).filter(Boolean)
    : DEFAULT_COLUMNS;
  const normalized = baseColumns.length > 0 ? baseColumns : DEFAULT_COLUMNS;
  const generatedColumns = normalizeGeneratedColumns(artifact);
  const existingKeys = new Set(normalized.map((column) => column.key));
  return [
    ...normalized,
    ...generatedColumns.filter((column) => !existingKeys.has(column.key)),
  ];
}

function normalizeAction(action) {
  if (!action || typeof action !== 'object') return null;
  const key = firstPresent(action.key, action.product_key, action.action_key);
  if (!key) return null;
  const normalized = {
    ...action,
    key,
    label: firstPresent(action.label, action.display_name, key),
    kind: firstPresent(action.kind, action.type, key),
    enabled: action.enabled !== false && action.disabled !== true,
    primary: action.primary === true,
  };
  const disabledReason = firstPresent(action.disabled_reason, action.disabledReason);
  if (disabledReason) normalized.disabled_reason = disabledReason;
  return normalized;
}

function replayAction(action, replayKey) {
  const metadata = action?.[replayKey];
  if (
    (metadata?.supported !== true && metadata?.supported !== 'true') ||
    !metadata?.endpoint?.path
  ) return null;
  const parentKey = firstPresent(action.action_key, action.key);
  if (!parentKey) return null;
  const retry = replayKey === 'retry';
  return normalizeAction({
    ...metadata,
    key: `${parentKey}_${retry ? 'retry_failed_rows' : 'refresh_all'}`,
    action_key: parentKey,
    label: firstPresent(metadata.label, retry ? 'Retry failed rows' : 'Refresh all'),
    kind: 'connector_replay',
    enabled: action.enabled !== false && metadata.enabled !== false && metadata.disabled !== true,
    requires_confirmation: metadata.requires_confirmation !== false,
    endpoint: metadata.endpoint,
    output_identity: firstPresent(metadata.output_identity, action.output_identity),
    cost_label: firstPresent(metadata.cost_label, action.cost_label),
    scope_label: firstPresent(metadata.scope_label, action.scope_label),
    scope_count: firstNumber(metadata.scope_count, action.scope_count),
  });
}

function expandAction(action) {
  if (!action) return [];
  return [
    action,
    replayAction(action, 'retry'),
    replayAction(action, 'refresh'),
  ].filter(Boolean);
}

function defaultExportAction(companyIds) {
  const enabled = companyIds.length > 0;
  return {
    key: 'export_csv',
    label: 'Export',
    kind: 'export',
    enabled,
    primary: true,
    ...(enabled ? {} : { disabled_reason: 'No companies to export.' }),
  };
}

function normalizeActions(artifact, companyIds) {
  if (Array.isArray(artifact?.actions) && artifact.actions.length > 0) {
    const normalized = [];
    artifact.actions.forEach((action) => {
      expandAction(normalizeAction(action)).forEach((item) => normalized.push(item));
    });
    return normalized.length > 0 ? normalized : [defaultExportAction(companyIds)];
  }
  return [defaultExportAction(companyIds)];
}

function normalizeSuggestions(artifact, hasRows) {
  if (!hasRows) return [];
  if (!artifact?.suggestions?.length) return [{ ...FIND_SIMILAR_SUGGESTION }];
  const backend = artifact.suggestions.find((s) => s?.key === FIND_SIMILAR_SUGGESTION.key);
  if (!backend) return [];
  return [{ ...FIND_SIMILAR_SUGGESTION, prompt: firstPresent(backend.prompt, FIND_SIMILAR_SUGGESTION.prompt) }];
}

function normalizeLineage(artifact) {
  const lineage = artifact?.derived_lineage;
  if (!lineage || typeof lineage !== 'object') return null;
  const parentArtifactId = firstPresent(lineage.parent_artifact_id, artifact?.parent_artifact_id);
  if (!parentArtifactId) return null;
  return {
    parentArtifactId,
    suggestionKey: firstPresent(lineage.suggestion_key, artifact?.suggestion_key),
    sourceAgeDaysAtDerivation: firstNumber(lineage.source_age_days_at_derivation),
  };
}

export function buildArtifactDisplayModel({
  artifactData = null,
  message = {},
  fallbackCompanies = [],
} = {}) {
  const artifact = artifactData?.artifact || message?.artifact || {};
  const coreResultSet = coreResultSetFromPayload(artifactData, message);
  const artifactCandidates = Array.isArray(artifactData?.candidates) ? artifactData.candidates : null;
  const messageCandidates = Array.isArray(message?.candidates) ? message.candidates : null;
  const legacyCandidates = Array.isArray(fallbackCompanies)
    ? fallbackCompanies.map(companyToCandidate)
    : [];
  const eligibilityCounts = normalizeEligibilityCounts(artifact);
  const requestedSampleLimit = firstNumber(artifact?.sample_limit);
  const sampleLimit = Math.max(0, Math.min(requestedSampleLimit ?? 5, 5));
  const { candidates, eligibleCandidates, sampleCandidates } = resolveCoreCandidates({
    coreResultSet,
    artifactCandidates,
    messageCandidates,
    legacyCandidates,
    eligibleCount: eligibilityCounts.eligible,
    sampleLimit,
  });
  const companyIds = eligibleCandidates.map(candidateId).filter(Boolean).map(String);
  const rows = (displayPreviewRows(artifactData) || sampleCandidates).map(candidateToDisplayRow);
  const totalCount = firstNumber(
    eligibilityCounts.eligible,
    artifact?.total_count,
    artifact?.candidate_count,
    artifact?.stored_candidate_count,
    eligibleCandidates.length
  );
  const hasRows = rows.length > 0;
  const leadSummary = buildLeadSummary(sampleCandidates, candidateToDisplayRow);
  const leadScopeCount = coreResultSet?.model?.completeRowCount ?? totalCount ?? candidates.length;
  const leadLockedHint = buildLeadLockedHint(leadSummary, sampleCandidates.length, leadScopeCount, artifact);
  const summary = coreResultSet ? null : firstPresent(
    artifact?.summary_text,
    artifact?.result_summary,
    artifact?.summary,
    hasRows ? null : artifact?.empty_message,
    message?.content,
    hasRows
      ? `Found ${totalCount ?? rows.length} ${totalCount === 1 ? 'company' : 'companies'}.`
      : 'I could not find matching companies for this request.'
  );
  return {
    artifactId: firstPresent(artifact?.artifact_id, artifact?.id, message?.artifactId, message?.artifact_id),
    title: firstPresent(artifact?.display_title, artifact?.title, artifact?.name, hasRows ? 'Target list results' : null),
    summary,
    sourceDescription: firstPresent(artifact?.source_description),
    totalCount,
    coreResultSet: coreResultSet?.model || null,
    exportCount: artifactExportScopeCount(
      artifact,
      companyIds,
      coreResultSet?.model?.completeRowCount
    ),
    eligibilityCounts,
    sampleCount: rows.length,
    sampleLabel: artifactTableLabel(displayPreviewRows(artifactData) ? null : coreResultSet, rows.length, coreResultSet?.model?.completeRowCount ?? totalCount),
    columns: normalizeColumns(artifact),
    rows,
    companyIds,
    leadSummary,
    leadLockedHint,
    actions: normalizeActions(artifact, companyIds),
    notices: normalizeNotices(artifact),
    suggestions: normalizeSuggestions(artifact, hasRows),
    artifactState: firstPresent(artifact?.artifact_state, artifactData?.artifact_state),
    artifactAgeDays: firstNumber(artifact?.artifact_age_days),
    expiresAt: firstPresent(artifact?.expires_at, artifact?.ttl_expires_at),
    lineage: normalizeLineage(artifact),
  };
}

export function artifactDisplayCompanyIds(message) {
  const displayIds = message?.artifactDisplay?.companyIds;
  if (Array.isArray(displayIds) && displayIds.length > 0) {
    return displayIds.filter(Boolean);
  }
  return (message?.companies || []).map((company) => company.id).filter(Boolean);
}

export function artifactActionByKey(artifactDisplay, key) {
  return (artifactDisplay?.actions || []).find((action) => action.key === key) || null;
}

export function latestArtifactMessage(messages) {
  return [...(messages || [])].reverse().find((message) => message?.artifactDisplay) || null;
}

export function composerArtifactActions(artifactDisplay) {
  const actions = Array.isArray(artifactDisplay?.actions) ? artifactDisplay.actions : [];
  return actions.filter(
    (action) =>
      action?.key &&
      action?.label &&
      action.key !== 'export_csv' &&
      !String(action.key).startsWith('answer_by_enriching') &&
      action.kind !== 'export'
  );
}
